import type {
    ListFilterRule,
    ListFilterScalarValue,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import type { CompanyExecutive } from '@/queries';

const normalizeExecutiveName = (value: string) =>
    value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z\d]+/gi, ' ')
        .trim()
        .toLocaleLowerCase();

export const findExecutiveProfile = (executives: CompanyExecutive[], executiveName: string) => {
    const normalizedName = normalizeExecutiveName(executiveName);
    const exactMatch = executives.find((executive) => normalizeExecutiveName(executive.name) === normalizedName);

    if (exactMatch) {
        return exactMatch;
    }

    const chiefExecutives = executives.filter((executive) =>
        /\b(ceo|chief executive(?: officer)?)\b/i.test(executive.title ?? ''),
    );

    return chiefExecutives.length === 1 ? chiefExecutives[0] : undefined;
};

const getExecutiveFilterValue = (executive: CompanyExecutive, field: string) => {
    switch (field) {
        case 'active':
            return executive.active;
        case 'gender':
            return executive.gender;
        case 'title':
            return executive.title;
        case 'titleSince':
            return executive.titleSince;
        case 'totalCompensation':
            return executive.compensation?.total ?? executive.pay;
        default:
            return undefined;
    }
};

const normalizeRuleValues = (value: ListFilterRule['value']) =>
    (Array.isArray(value) ? value : [value]).filter(
        (item): item is ListFilterScalarValue => item !== '' && item !== null && item !== undefined,
    );

const matchesFilterValue = (
    actualValue: boolean | number | string | undefined,
    expectedValue: ListFilterScalarValue,
    rule: ListFilterRule,
) => {
    if (actualValue === undefined) {
        return rule.operator === 'notEquals';
    }

    if (typeof actualValue === 'number') {
        const expectedNumber = typeof expectedValue === 'number' ? expectedValue : Number(expectedValue);

        if (!Number.isFinite(expectedNumber)) {
            return false;
        }

        switch (rule.operator) {
            case 'greaterThan':
                return actualValue > expectedNumber;
            case 'lessThan':
                return actualValue < expectedNumber;
            case 'notEquals':
                return actualValue !== expectedNumber;
            case 'equals':
            default:
                return actualValue === expectedNumber;
        }
    }

    if (typeof actualValue === 'boolean') {
        const expectedBoolean = expectedValue === true || expectedValue === 'true';

        return rule.operator === 'notEquals' ? actualValue !== expectedBoolean : actualValue === expectedBoolean;
    }

    const actualText = actualValue.trim().toLocaleLowerCase();
    const expectedText = String(expectedValue).trim().toLocaleLowerCase();

    switch (rule.operator) {
        case 'contains':
            return actualText.includes(expectedText);
        case 'notEquals':
            return actualText !== expectedText;
        case 'equals':
        default:
            return actualText === expectedText;
    }
};

export const filterCompanyExecutives = (executives: CompanyExecutive[], query: string, rules: ListFilterRule[]) => {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    return executives.filter((executive) => {
        const matchesQuery =
            !normalizedQuery ||
            [
                executive.name,
                executive.title,
                executive.gender,
                executive.location,
                executive.email,
                executive.titleSince?.toString(),
            ].some((value) => value?.toLocaleLowerCase().includes(normalizedQuery));

        return (
            matchesQuery &&
            rules.every((rule) => {
                const expectedValues = normalizeRuleValues(rule.value);
                const actualValue = getExecutiveFilterValue(executive, rule.field);
                const matches = expectedValues.map((expectedValue) =>
                    matchesFilterValue(actualValue, expectedValue, rule),
                );
                const join = rule.valueJoin ?? (rule.operator === 'notEquals' ? 'and' : 'or');

                return join === 'and' ? matches.every(Boolean) : matches.some(Boolean);
            })
        );
    });
};
